import React, { useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Copy,
  FileJson,
  Loader2,
  Megaphone,
  Package,
  RefreshCw,
  Sparkles,
  Upload,
} from 'lucide-react';
import { aiService } from '../../services/aiService';
import { LANGUAGES } from '../../selectors/LanguageSelector';
import MediaAssetStudio, { MEDIA_ASSET_SPECS, renderAsset } from './MediaAssetStudio';
import { LANDSCAPE_VARIATIONS, mediaAssetConfig } from './mediaAssetConfig';
import InstagramStorySeriesStudio from './InstagramStorySeriesStudio';
import MediaLaunchCalendar from './MediaLaunchCalendar';
import { loadMediaPackageDraft, saveMediaPackageDraft } from './mediaPackageDraftStore';
import {
  loadMediaPackageCloudDraft,
  loadMediaPackageLayoutPreference,
  loadLatestReusablePhoneMockup,
  getMediaPackageStorageBlob,
  getMediaPackageStorageDownloadUrl,
  mediaPackageWorkspaceId,
  publishMediaPackageCloudSnapshot,
  saveMediaPackageCloudDraft,
  saveMediaPackageLayoutPreference,
} from './mediaPackageCloudStore';
import {
  applyMediaPackageLayoutPreset,
  extractMediaPackageLayoutPreset,
  preserveMediaPackageFormatLayouts,
} from './mediaPackageLayoutPreset';
import {
  MEDIA_CHANNELS,
  buildMediaGuideFacts,
  createMediaPackage,
  mediaPackageFilename,
  mergeAiMediaChannelDraft,
  refreshMediaPackageFromGuide,
  validateMediaPackage,
} from './mediaPackageSchema';
import {
  allCopyFilePath,
  buildAllCopy,
  buildMediaPackageReadme,
  copyFilePath,
  imageFilePath,
  mainPreviewVideoFilePath,
  packageDataFilePath,
  storyCopyFilePath,
  storyFolderPath,
  storyVideoFilePath,
} from './mediaPackageZipLayout';
import { ensureCaptionLink, finalizeSocialCaption } from './mediaPackageSocialCopy';
import {
  createMediaPackageConfigFile,
  parseMediaPackageConfigFile,
} from './mediaPackageConfigFile';

const AI_MODEL = 'gpt-5.6-sol';
const AI_REASONING_EFFORT = 'medium';

const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

const SETUP_FIELDS = [
  {
    key: 'output_language',
    label: 'Media package language',
    required: true,
    options: LANGUAGES.map(({ code, name }) => ({ value: code, label: name })),
    hint: 'All AI-generated public copy will be written naturally in this language.',
  },
  {
    key: 'organisation',
    label: 'Client / publishing organisation',
    required: true,
    hint: 'The museum, venue, municipality, or client presented as the sender of the announcement.',
  },
  {
    key: 'partner_social_handle',
    label: 'Partner Instagram handle',
    required: true,
    placeholder: '@museumname',
    hint: 'Required for social copy. This is combined automatically with @guide.sofia.',
  },
  { key: 'venue', label: 'Venue or destination', required: true },
  { key: 'guide_title', label: 'Guide title', required: true },
  {
    key: 'public_guide_url',
    label: 'Whole Guide QR link',
    required: true,
    type: 'url',
    readOnly: true,
    hint: 'Filled automatically from the same Whole Guide QR used in the Stops tab.',
  },
  {
    key: 'short_url',
    label: 'Public campaign link',
    required: true,
    type: 'url',
    placeholder: 'https://guidesofia.com/cascais',
    hint: 'Required before generating copy. Paste the complete public-friendly URL, including https://.',
  },
  { key: 'launch_date', label: 'Launch date', required: true, type: 'date' },
  {
    key: 'content_type',
    label: 'Public experience name',
    required: true,
    options: [
      { value: 'audio_guide', label: 'Audio guide' },
      { value: 'multimedia_guide', label: 'Multimedia guide' },
      { value: 'digital_route', label: 'Digital visitor route' },
      { value: 'custom', label: 'Other / custom' },
    ],
    hint: 'Audio guide is the default. Choose another description only when the experience is genuinely different.',
  },
  { key: 'custom_content_name', label: 'Custom experience name', hint: 'Used only when “Other / custom” is selected.' },
  {
    key: 'personalized_ai_enabled',
    label: 'Visitors can generate personalized AI guides',
    type: 'checkbox',
    hint: 'When unchecked, communications simply describe the curated audio stories. They never explain that the audio is pre-recorded.',
  },
  { key: 'price', label: 'Price or access condition', required: true, placeholder: 'Free' },
  { key: 'start_point', label: 'Where visitors begin', required: true },
  { key: 'access_method', label: 'Access method', required: true },
  {
    key: 'languages',
    label: 'Available guide languages',
    required: true,
    multiple: true,
    options: LANGUAGES.map(({ code, name }) => ({ value: code, label: name })),
    hint: 'Select every language visitors can use. Portuguese (Portugal) and Portuguese (Brazil) are separate options.',
  },
  { key: 'client_quote', label: 'Approved client quote', multiline: true },
  { key: 'quote_attribution', label: 'Quote attribution', placeholder: 'Name, role, organisation' },
  { key: 'press_name', label: 'Press contact name' },
  { key: 'press_email', label: 'Press contact email', type: 'email' },
  { key: 'press_phone', label: 'Press contact phone' },
  { key: 'technology_credit', label: 'Technology credit' },
  {
    key: 'guidesofia_brand_fact',
    label: 'Approved GuideSofia brand message',
    multiline: true,
    hint: 'Used as an approved fact in website, newsletter, social, and press copy. Edit it if the catalogue claim changes.',
  },
  { key: 'hashtags', label: 'Approved hashtags', placeholder: '#guidesofia #audioguide ...' },
];

const cleanJsonResponse = (value) => (
  String(value || '')
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim()
);

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30000);
};

function CollapsiblePanel({ title, description, children, defaultOpen = false, tone = 'slate' }) {
  const toneClasses = tone === 'fuchsia'
    ? 'border-fuchsia-200 bg-fuchsia-50/40'
    : tone === 'indigo'
      ? 'border-indigo-200 bg-indigo-50/40'
      : tone === 'emerald'
        ? 'border-emerald-200 bg-emerald-50/40'
        : tone === 'amber'
          ? 'border-amber-200 bg-amber-50/40'
          : 'border-slate-200 bg-white';

  return (
    <details {...(defaultOpen ? { open: true } : {})} className={`group overflow-hidden rounded-2xl border shadow-sm ${toneClasses}`}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 marker:hidden [&::-webkit-details-marker]:hidden">
        <span>
          <span className="block font-semibold text-slate-900">{title}</span>
          {description && <span className="mt-1 block text-sm font-normal leading-5 text-slate-500">{description}</span>}
        </span>
        <ChevronDown className="h-5 w-5 shrink-0 text-slate-500 transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-slate-200/80 p-5">{children}</div>
    </details>
  );
}

const CHANNEL_REQUIREMENTS = {
  website: [
    'Write 180 to 260 words.',
    'Start with an editorial headline, then open on one concrete contradiction, human detail, or surprising story from the guide. Do not open by announcing that a guide exists.',
    'Use three to five short paragraphs: story hook, what the visitor can experience, what is innovative, the GuideSofia partnership and wider free catalogue, then a clear visitor action.',
    'Make the page useful to a first-time visitor and interesting enough to read even before they decide to open the guide.',
  ].join(' '),
  instagram_facebook: [
    'Write a 90 to 150 word evergreen caption with a first line that earns attention through a specific story detail, contradiction, or unanswered question.',
    'Use short paragraphs and natural rhythm. Name the audio guide, the client, GuideSofia, and the relevant innovation without sounding like a product announcement.',
    'Mention the wider free GuideSofia catalogue in Portugal in one concise, natural sentence.',
    'Finish with an explicit invitation to download, open, or try the audio guide, followed by the exact approved audioguide URL, @mentions, and hashtags. Merely saying that the story is told in the audio guide is not a call to action. Do not write labels such as Hook, Caption, or CTA.',
  ].join(' '),
  newsletter: [
    'Return a subject line, preview text, 180 to 260 words of body copy, and a short button label.',
    'Open with a human or historical story, then explain how the audio guide changes the visit. Include the client and GuideSofia partnership, the relevant innovation, and the wider free catalogue in Portugal.',
    'Write warmly and directly for people who already care about the venue. Avoid corporate launch language.',
  ].join(' '),
  press_release: [
    'Write a credible 450 to 650 word press release with headline, subheading, body, practical information, supplied quote only, media contact, suggested image caption, photo-credit placeholder, and a short About GuideSofia boilerplate. Do not include a dated dateline.',
    'The news angle is not merely that an audio guide exists. Explain what becomes newly possible for visitors, how the guide uses stories, and what GuideSofia contributes as creative and technology partner.',
    'Include the approved fact about hundreds of free GuideSofia guides across Portugal. Keep the voice journalistic and vivid, with restrained wit rather than sarcasm.',
    'Never fabricate a quote, statistic, award, reaction, or institutional claim.',
  ].join(' '),
};

const buildAiPrompt = (packageData, channelId) => {
  const publicLanguageNames = {
    'en-US': 'English',
    'pt-PT': 'Portuguese',
    'pt-BR': 'Brazilian Portuguese',
    'es-ES': 'Spanish',
    'fr-FR': 'French',
    'de-DE': 'German',
    'it-IT': 'Italian',
    'nl-NL': 'Dutch',
    'ru-RU': 'Russian',
    'bg-BG': 'Bulgarian',
  };
  const publicLanguageName = (code) => publicLanguageNames[code] || String(code || '').split('-')[0] || 'the selected language';
  const outputLanguageName = publicLanguageName(packageData.config.output_language);
  const selectedIds = new Set(packageData.config.selected_stop_ids || []);
  const selectedStops = packageData.guide.stops
    .filter((stop) => selectedIds.has(stop.id))
    .filter((stop) => String(stop.in_depth_description || '').trim())
    .map((stop) => ({
      stop_id: stop.id,
      name: stop.name,
      approved_in_depth_story: stop.in_depth_description,
    }));
  if (selectedStops.length === 0) {
    throw new Error('The selected stops do not have an inDepthDescription. Add approved in-depth content or select different stories.');
  }
  const channel = MEDIA_CHANNELS.find((item) => item.id === channelId);
  const contentNames = {
    audio_guide: 'audio guide',
    multimedia_guide: 'multimedia guide',
    digital_route: 'digital visitor route',
    custom: packageData.config.custom_content_name || 'visitor experience',
  };
  const publicContentName = contentNames[packageData.config.content_type] || 'audio guide';
  const localizedConfig = {
    ...packageData.config,
    output_language: outputLanguageName,
    languages: (Array.isArray(packageData.config.languages) ? packageData.config.languages : [])
      .map(publicLanguageName),
    public_guide_link_available: Boolean(packageData.config.short_url),
    approved_public_url: packageData.config.short_url,
  };
  ['public_guide_url', 'short_url', 'launch_date', 'duration', 'installation_answer', 'accessibility', 'support_contact', 'institutional_objective']
    .forEach((key) => delete localizedConfig[key]);
  const formatInstructions = String(packageData.config.channel_instructions?.[channelId] || '').trim();

  return [
    `Create the ${channel?.label || channelId} item for the approved ${publicContentName} below.`,
    '',
    'APPROVED FACTS AND CLIENT SAFETY:',
    '- Treat GUIDE FACTS, CLIENT CONFIGURATION, and SELECTED APPROVED STOPS as the complete source of truth.',
    '- Use only approved_in_depth_story for historical, human, cultural, or natural claims. Never invent or research extra facts for this task.',
    '- Never invent dates, prices, languages, accessibility, visitor numbers, awards, testimonials, quotes, reactions, features, or institutional claims.',
    '- Preserve proper names, prices, contact details, hashtags, and @username mentions exactly.',
    '- Print approved_public_url exactly once in the public draft. Never invent, shorten, or alter the URL.',
    '- This communication must be sellable to and approvable by the museum, venue, municipality, or client. Never ridicule, embarrass, undermine, or criticize the institution or its community.',
    '- Gentle irony may illuminate a clearly supported human contradiction or decision, but it must remain appropriate for an institutional channel.',
    '- Present the guide and client as the subject of the communication. Name GuideSofia clearly as the creative and technology partner, not as a hidden technical credit and not as a brand that overwhelms the client.',
    '- Treat guidesofia_brand_fact as an approved brand fact. Translate it naturally into the output language and include it where required by the format brief.',
    '',
    'SOFIA EDITORIAL VOICE:',
    '- Write with Sofia’s editorial intelligence: savvy, lightly cynical, deeply knowledgeable, conversational, and alert to the human or natural grit behind a place.',
    '- Sofia hates Museum Voice. If a sentence sounds like a tourism brochure, corporate announcement, grant application, or plaque, rewrite it.',
    '- Lead with meaning and story, not features. Prefer a specific person, action, contradiction, consequence, odd detail, or unresolved question.',
    '- Tell the reader why something matters. Do not deposit facts, recite inventories, summarize curricula, or stack adjectives.',
    '- Use modern common sense sparingly to clarify the human stakes. Remain politically neutral and do not promote left-wing or right-wing talking points.',
    '- Use dates as context rather than beginning a sentence with a bare date.',
    '- Vary sentence length. Use concrete verbs, fluid transitions, and natural spoken energy even in written formats.',
    '- Do not force first-person Sofia narration into institutional formats. Preserve Sofia’s sensibility while using the format-appropriate point of view.',
    '- Do not use: represents, stands as, testament to, must-see, stunning, breathtaking, hidden gem, flex, innovative solution, or new way to explore.',
    '- Never begin with: Welcome to, You are standing at, Look at, Notice, See this, or Olha para.',
    '- Do not use em dashes or en dashes. Use commas, full stops, colons, or parentheses.',
    '- Avoid unsupported superlatives, fake suspense, clickbait, filler, and vague claims such as bringing history to life.',
    '',
    'GUIDESOFIA AND THE INNOVATION:',
    '- Explain the innovation through the visitor benefit, not through startup jargon.',
    `- Describe the product as an ${publicContentName}; never call it a "digital guide" unless that exact custom content name was selected.`,
    packageData.config.personalized_ai_enabled
      ? '- The experience combines curated audio stories with the option for visitors to generate personalized AI guides. Explain the benefit clearly without implying unrestricted, unsupervised, or unsupported AI features.'
      : '- Describe the experience simply as an audio guide, audio stories, or stories to hear. Never call the content pre-recorded, prerecorded, recorded, static, fixed, or "only". Do not mention personalized, generated, conversational, or AI-created guides when that feature is disabled.',
    '- Mention the Whole Guide link or QR as an easy route into the experience when useful, without turning the copy into technical instructions.',
    '',
    'LANGUAGE AND DELIVERY:',
    `- Write every public-facing word naturally in ${outputLanguageName}.`,
    '- The target Media Package language overrides the source-guide language.',
    '- Write idiomatically for the selected locale. Do not translate English marketing formulas literally.',
    ...(packageData.config.output_language === 'pt-PT' ? [
      '- For European Portuguese, use an informal, direct, contemporary register and address the reader with tu forms.',
      '- Never address the reader as você. Avoid formal constructions such as poderá, deverá, consigo, convidamo-lo, or permita-se descobrir.',
      '- Do not repeat the pronoun tu unnecessarily. Prefer natural second-person verbs and concise spoken phrasing used in Portugal.',
      '- In Portuguese, never use pré-gravado, pré-gravada, pré-gravados, or pré-gravadas. Simply call them histórias áudio or an audioguia.',
    ] : []),
    ...(packageData.config.output_language === 'pt-BR' ? [
      '- For Brazilian Portuguese, use a natural, informal Brazilian register. Do not imitate European Portuguese vocabulary or verb forms.',
    ] : []),
    '- Never expose locale codes such as pt-PT, pt-BR, en-US, or es-ES. Use normal audience-facing language names such as Português, Português do Brasil, English, or Español.',
    '- For Instagram/Facebook copy, include every handle in social_mentions exactly, including @guide.sofia and the required partner handle. Never invent another account.',
    '- Write evergreen copy in the present tense. The guide is already online when the audience reads this.',
    '- Never mention a launch date, publication date, "available from", "launching on", "newly launched", or similar time-sensitive wording.',
    '- Include approved_public_url on its own line after an explicit call to action.',
    '- Return valid JSON only, with no markdown fences and no commentary.',
    '',
    'OUTPUT JSON:',
    JSON.stringify({
      story_promises: [
        { stop_id: 'exact selected stop ID', title: 'short public-facing story promise', hook: 'one accurate sentence' },
      ],
      visual_copy: {
        headline: 'specific, intriguing campaign headline, maximum 8 words, never a generic launch formula',
        subtitle: 'meaningful supporting line, maximum 12 words, adding information rather than repeating the headline',
        cta: 'short call to action, maximum 6 words',
        alt_text: 'accurate accessible description based only on the configured image description',
      },
      drafts: { [channelId]: 'complete, interesting, publication-ready copy optimized specifically for this format' },
    }, null, 2),
    '',
    'FORMAT REQUIREMENT:',
    CHANNEL_REQUIREMENTS[channelId] || 'Write complete publication-ready copy for this format.',
    ...(formatInstructions ? [
      '',
      'EDITORIAL INSTRUCTIONS FOR THIS FORMAT:',
      formatInstructions,
      'Follow these instructions when they do not conflict with the approved facts or safety rules above.',
    ] : []),
    '',
    'GUIDE FACTS:',
    JSON.stringify({
      guide_title: packageData.guide.guide_title,
      guide_description: packageData.guide.guide_description,
      venue: packageData.guide.venue,
      city: packageData.guide.city,
      country: packageData.guide.country,
      language: publicLanguageName(packageData.guide.language),
      number_of_stops: packageData.guide.number_of_stops,
      duration: packageData.guide.duration,
    }, null, 2),
    '',
    'CLIENT CONFIGURATION:',
    JSON.stringify(localizedConfig, null, 2),
    '',
    'SELECTED APPROVED STOPS:',
    JSON.stringify(selectedStops, null, 2),
  ].join('\n');
};

const validatePublicMediaOutput = (parsed, channelId, packageData) => {
  const publicOutput = JSON.stringify({
    draft: parsed?.drafts?.[channelId] || '',
    visual_copy: parsed?.visual_copy || {},
    story_promises: parsed?.story_promises || [],
  });
  const violations = [];

  const urls = publicOutput.match(/https?:\/\/[^\s"\\]+/gi) || [];
  const approvedPublicUrl = packageData.config.short_url || '';
  const normalizedUrls = urls.map((url) => url.replace(/[),.;!?]+$/g, ''));
  if (!normalizedUrls.includes(approvedPublicUrl)) violations.push('the approved public link is missing');
  if (normalizedUrls.some((url) => url !== approvedPublicUrl)) {
    violations.push('an unapproved URL');
  }
  if (/\b(?:pt-PT|pt-BR|en-US|es-ES|fr-FR|de-DE|it-IT|nl-NL|ru-RU|bg-BG)\b/i.test(publicOutput)) {
    violations.push('an internal language code');
  }
  if (/\bpre[-\s]?recorded\b/i.test(publicOutput) || /\bpr[eé][\s-]?gravad[oa]s?\b/iu.test(publicOutput)) {
    violations.push('pre-recorded wording');
  }
  if (packageData.config.output_language === 'pt-PT' && /\bvoc[eê]\b/iu.test(publicOutput)) {
    violations.push('the formal address "você"');
  }
  if (packageData.config.launch_date && publicOutput.includes(packageData.config.launch_date)) {
    violations.push('the internal launch date');
  }

  if (violations.length > 0) {
    throw new Error(`The generated text included ${violations.join(', ')}. It was not saved. Generate this format again.`);
  }
};

function Field({ field, value, onChange }) {
  return (
    <label className={field.multiline ? 'md:col-span-2' : ''}>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {field.label}
        {field.required && <span className="ml-1 text-rose-500">*</span>}
      </span>
      {field.multiline ? (
        <textarea
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          rows={3}
          placeholder={field.placeholder || ''}
          className={`${inputClass} resize-y`}
        />
      ) : field.multiple ? (
        <select
          multiple
          size={Math.min(6, field.options?.length || 6)}
          value={Array.isArray(value) ? value : String(value || '').split(',').map((item) => item.trim()).filter(Boolean)}
          onChange={(event) => onChange(Array.from(event.target.selectedOptions, (option) => option.value))}
          className={`${inputClass} min-h-[9.5rem]`}
        >
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      ) : field.type === 'checkbox' ? (
        <span className="flex min-h-[42px] items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => onChange(event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
          />
          <span className="text-sm font-medium normal-case tracking-normal text-slate-700">{value ? 'Enabled' : 'Not enabled'}</span>
        </span>
      ) : field.options ? (
        <select
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          className={inputClass}
        >
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      ) : (
        <input
          type={field.type || 'text'}
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder || ''}
          readOnly={field.readOnly}
          className={`${inputClass} ${field.readOnly ? 'cursor-not-allowed bg-slate-100 text-slate-500' : ''}`}
        />
      )}
      {field.hint && <span className="mt-1 block text-xs text-slate-400">{field.hint}</span>}
    </label>
  );
}

function GuideSourceSummary({ packageData }) {
  const guide = packageData.guide;
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">Guide source</div>
          <h3 className="mt-1 font-semibold text-slate-900">{guide.guide_title || 'Untitled guide'}</h3>
          <p className="mt-1 text-sm text-slate-600">
            {[guide.city, guide.country, guide.language].filter(Boolean).join(' | ')}
          </p>
        </div>
        <div className="rounded-lg bg-white px-3 py-2 text-center shadow-sm">
          <div className="text-xl font-bold text-slate-900">{guide.number_of_stops}</div>
          <div className="text-xs text-slate-500">approved stops</div>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-700">
        {guide.guide_description || 'Add a guide description in Guide Info before generating final copy.'}
      </p>
    </div>
  );
}

function DraftCard({
  channel,
  value,
  instructions,
  onInstructionsChange,
  onChange,
  onCopy,
  copied,
  onGenerate,
  isGenerating,
  generationError,
}) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{channel.group}</div>
          <h3 className="font-semibold text-slate-900">{channel.label}</h3>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onGenerate} disabled={isGenerating} className="flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700 disabled:bg-slate-400">
            {isGenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {isGenerating ? 'Generating...' : 'Generate this format'}
          </button>
          <button type="button" onClick={onCopy} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </header>
      <div className="border-b border-slate-100 bg-sky-50/50 p-4">
        <label className="text-xs font-semibold uppercase tracking-wide text-sky-800">Instructions for this format
          <textarea
            value={instructions || ''}
            onChange={(event) => onInstructionsChange(event.target.value)}
            rows={3}
            placeholder={`Example: For this ${channel.label}, focus on the restoration story and mention the opening-week programme.`}
            className={`${inputClass} mt-1 resize-y bg-white normal-case leading-5`}
          />
        </label>
        <p className="mt-1 text-xs text-slate-500">These directions are sent only when generating this format and are saved with the Media Package.</p>
      </div>
      <textarea
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        rows={12}
        className="min-h-[18rem] w-full resize-y rounded-b-xl border-0 bg-white p-4 text-sm leading-6 text-slate-800 outline-none focus:ring-2 focus:ring-inset focus:ring-sky-100"
      />
      {generationError && <div className="border-t border-rose-100 bg-rose-50 px-4 py-3 text-xs text-rose-800">{generationError}</div>}
    </article>
  );
}

const MediaPackageTab = ({ itinerary, setItinerary }) => {
  const guideKey = `${itinerary?.metadata?.id || itinerary?.metadata?.name || 'new'}::${itinerary?.metadata?.language || 'en'}`;
  const [packageData, setPackageData] = useState(() => (
    createMediaPackage(itinerary, itinerary?._builder?.mediaPackage)
  ));
  const [generatingChannel, setGeneratingChannel] = useState('');
  const [channelErrors, setChannelErrors] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [copiedChannel, setCopiedChannel] = useState('');
  const [generatedAssets, setGeneratedAssets] = useState({});
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [packageActionFeedback, setPackageActionFeedback] = useState(null);
  const [publishedInfo, setPublishedInfo] = useState(null);
  const [factsRefreshStatus, setFactsRefreshStatus] = useState('idle');
  const [factsRefreshedAt, setFactsRefreshedAt] = useState('');
  const [localCampaignImage, setLocalCampaignImage] = useState('');
  const [localPartnerLogo, setLocalPartnerLogo] = useState('');
  const [localPhoneFrame, setLocalPhoneFrame] = useState('');
  const [localPhoneScreen, setLocalPhoneScreen] = useState('');
  const [localVideoMedia, setLocalVideoMedia] = useState(null);
  const [cloudAssets, setCloudAssets] = useState({});
  const [loadedDraftKey, setLoadedDraftKey] = useState('');
  const [draftStorageStatus, setDraftStorageStatus] = useState('loading');
  const [draftStorageError, setDraftStorageError] = useState('');
  const [layoutPreferenceStatus, setLayoutPreferenceStatus] = useState('');
  const [layoutPreference, setLayoutPreference] = useState(null);
  const cloudAssetsRef = useRef({});
  const cloudSaveQueueRef = useRef(Promise.resolve());
  const factsRefreshTimerRef = useRef(null);
  const phoneTemplateSavedSignatureRef = useRef('');
  const configurationFileInputRef = useRef(null);
  const activeGuideKeyRef = useRef(guideKey);
  const localVideoMediaUrl = useMemo(() => (
    localVideoMedia ? URL.createObjectURL(localVideoMedia) : ''
  ), [localVideoMedia]);
  const workspaceId = mediaPackageWorkspaceId(guideKey);
  const storyMediaUrl = localVideoMediaUrl || cloudAssets.storyMedia?.url || '';
  const storyMediaType = localVideoMedia?.type || cloudAssets.storyMedia?.contentType || '';

  useEffect(() => () => {
    if (localVideoMediaUrl) URL.revokeObjectURL(localVideoMediaUrl);
    if (factsRefreshTimerRef.current) window.clearTimeout(factsRefreshTimerRef.current);
  }, [localVideoMediaUrl]);

  useEffect(() => {
    cloudAssetsRef.current = cloudAssets;
  }, [cloudAssets]);

  useEffect(() => {
    activeGuideKeyRef.current = guideKey;
  }, [guideKey]);

  const currentFacts = useMemo(() => buildMediaGuideFacts(itinerary), [itinerary]);
  const validationErrors = useMemo(() => validateMediaPackage(packageData), [packageData]);
  const selectedIds = packageData.config.selected_stop_ids || [];
  const activeInstagramStory = (Array.isArray(packageData.config.instagram_story_series)
    ? packageData.config.instagram_story_series
    : []).find((story) => (
    story.id === packageData.config.instagram_story_active_id
    && story.audio_url
    && !story.audio_stale
  ));
  const sourceChanged = (
    currentFacts.guide_title !== packageData.guide.guide_title
    || currentFacts.number_of_stops !== packageData.guide.number_of_stops
    || currentFacts.language !== packageData.guide.language
    || JSON.stringify(currentFacts.stops.map((stop) => [stop.id, stop.name, stop.in_depth_description]))
      !== JSON.stringify(packageData.guide.stops.map((stop) => [stop.id, stop.name, stop.in_depth_description]))
  );
  const reusablePhoneFrameUrl = cloudAssets.phoneFrame?.url || packageData.config.phone_frame_url || '';
  const reusablePhoneLayout = useMemo(() => {
    const phone = extractMediaPackageLayoutPreset(packageData.config).phone || {};
    return reusablePhoneFrameUrl ? { ...phone, phone_frame_url: reusablePhoneFrameUrl } : phone;
  }, [packageData.config, reusablePhoneFrameUrl]);
  const reusablePhoneLayoutSignature = JSON.stringify(reusablePhoneLayout);

  useEffect(() => {
    if (loadedDraftKey !== guideKey || !reusablePhoneFrameUrl || !reusablePhoneLayoutSignature) return undefined;
    if (phoneTemplateSavedSignatureRef.current === reusablePhoneLayoutSignature) return undefined;
    const timer = window.setTimeout(() => {
      const nextLayout = {
        ...(layoutPreference || extractMediaPackageLayoutPreset(packageData.config)),
        phone: reusablePhoneLayout,
      };
      saveMediaPackageLayoutPreference(nextLayout)
        .then(() => {
          phoneTemplateSavedSignatureRef.current = reusablePhoneLayoutSignature;
          setLayoutPreference(nextLayout);
        })
        .catch(() => undefined);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [guideKey, layoutPreference, loadedDraftKey, packageData.config, reusablePhoneFrameUrl, reusablePhoneLayout, reusablePhoneLayoutSignature]);

  useEffect(() => {
    let cancelled = false;
    setLoadedDraftKey('');
    setDraftStorageStatus('loading');
    setDraftStorageError('');
    Promise.allSettled([
      loadMediaPackageCloudDraft(guideKey),
      loadMediaPackageDraft(guideKey),
      loadMediaPackageLayoutPreference(),
      loadLatestReusablePhoneMockup(),
    ])
      .then(([cloudResult, localResult, preferenceResult, latestPhoneResult]) => {
        if (cancelled) return;
        const cloudDraft = cloudResult.status === 'fulfilled' ? cloudResult.value : null;
        setPublishedInfo(cloudDraft?.published || null);
        const localDraft = localResult.status === 'fulfilled' ? localResult.value : null;
        const restoredPackage = cloudDraft?.packageData
          || localDraft?.packageData
          || itinerary?._builder?.mediaPackage;
        const existingLayout = preferenceResult.status === 'fulfilled' ? preferenceResult.value : null;
        const latestPhone = latestPhoneResult.status === 'fulfilled' ? latestPhoneResult.value : null;
        const recoveredPhoneLayout = latestPhone?.config
          ? extractMediaPackageLayoutPreset(latestPhone.config).phone
          : null;
        const savedLayout = recoveredPhoneLayout && (!existingLayout?.phone || !existingLayout.phone.phone_frame_url)
          ? { ...(existingLayout || {}), phone: recoveredPhoneLayout }
          : existingLayout;
        setLayoutPreference(savedLayout);
        if (savedLayout && savedLayout !== existingLayout) {
          saveMediaPackageLayoutPreference(savedLayout).catch(() => undefined);
        }
        const restoredAssets = cloudDraft?.assets || localDraft?.cloudAssets || {};
        cloudAssetsRef.current = restoredAssets;
        setCloudAssets(restoredAssets);
        const restoredMediaPackage = createMediaPackage(itinerary, restoredPackage);
        const shouldApplyReusablePhone = Boolean(
          savedLayout?.phone?.phone_frame_url
          && !restoredMediaPackage.config.phone_frame_url
          && !restoredAssets.phoneFrame?.url
        );
        const nextMediaPackage = !restoredPackage && savedLayout
          ? {
            ...restoredMediaPackage,
            config: applyMediaPackageLayoutPreset(restoredMediaPackage.config, savedLayout),
          }
          : shouldApplyReusablePhone
            ? {
              ...restoredMediaPackage,
              config: { ...restoredMediaPackage.config, ...savedLayout.phone },
            }
            : restoredMediaPackage;
        setPackageData(nextMediaPackage);
        setLocalCampaignImage(restoredAssets.campaignImage?.url || localDraft?.localCampaignImage || '');
        setLocalPartnerLogo(restoredAssets.partnerLogo?.url || localDraft?.localPartnerLogo || '');
        setLocalPhoneFrame(restoredAssets.phoneFrame?.url || localDraft?.localPhoneFrame || (shouldApplyReusablePhone ? savedLayout.phone.phone_frame_url : ''));
        setLocalPhoneScreen(restoredAssets.phoneScreen?.url || localDraft?.localPhoneScreen || '');
        setLocalVideoMedia(cloudDraft?.assets?.storyMedia ? null : localDraft?.localVideoMedia || null);
        setLoadedDraftKey(guideKey);
        setDraftStorageStatus(cloudDraft ? 'saved' : 'ready');
        setMessage(cloudDraft
          ? 'Shared Media Package restored from Firebase.'
          : localDraft
            ? 'Local Media Package restored and ready to migrate to Firebase.'
            : '');
        if (cloudResult.status === 'rejected') {
          setDraftStorageError(`Firebase load failed; using the local fallback. ${cloudResult.reason?.message || ''}`.trim());
        }
        setError('');
      })
      .catch((storageError) => {
        if (cancelled) return;
        setPackageData(createMediaPackage(itinerary, itinerary?._builder?.mediaPackage));
        setLocalCampaignImage('');
        setLocalPartnerLogo('');
        setLocalPhoneFrame('');
        setLocalPhoneScreen('');
        setLocalVideoMedia(null);
        cloudAssetsRef.current = {};
        setCloudAssets({});
        setLoadedDraftKey(guideKey);
        setDraftStorageStatus('error');
        setDraftStorageError(storageError.message || 'Persistent Media Package storage is unavailable.');
      });
    return () => { cancelled = true; };
    // The guide key is the persistence boundary. In-guide edits are merged by the existing refresh flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideKey]);

  useEffect(() => {
    if (loadedDraftKey !== guideKey) return undefined;
    setDraftStorageStatus('saving');
    setDraftStorageError('');
    const timer = window.setTimeout(() => {
      const assetsSnapshot = cloudAssetsRef.current;
      const localSave = saveMediaPackageDraft(guideKey, {
        packageData,
        localCampaignImage,
        localPartnerLogo,
        localPhoneFrame,
        localPhoneScreen,
        localVideoMedia,
        cloudAssets: assetsSnapshot,
      });
      cloudSaveQueueRef.current = cloudSaveQueueRef.current
        .catch(() => undefined)
        .then(() => saveMediaPackageCloudDraft({
          guideKey,
          packageData,
          localCampaignImage,
          localPartnerLogo,
          localPhoneFrame,
          localPhoneScreen,
          localVideoMedia,
          previousAssets: activeGuideKeyRef.current === guideKey
            ? cloudAssetsRef.current
            : assetsSnapshot,
        }))
        .then((savedCloud) => {
          if (activeGuideKeyRef.current === guideKey) {
            cloudAssetsRef.current = savedCloud.assets;
            setCloudAssets(savedCloud.assets);
            setDraftStorageStatus('saved');
          }
        });
      Promise.all([localSave, cloudSaveQueueRef.current])
        .catch((storageError) => {
          if (activeGuideKeyRef.current !== guideKey) return;
          setDraftStorageStatus('error');
          setDraftStorageError(`${storageError.message || 'Firebase autosave failed.'} A browser-local fallback is still retained.`);
        });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [guideKey, loadedDraftKey, localCampaignImage, localPartnerLogo, localPhoneFrame, localPhoneScreen, localVideoMedia, packageData]);

  useEffect(() => {
    if (!setItinerary || loadedDraftKey !== guideKey) return;
    setItinerary((current) => {
      if (current?._builder?.mediaPackage === packageData) return current;
      return {
        ...current,
        _builder: {
          ...(current._builder || {}),
          mediaPackage: packageData,
        },
      };
    });
  }, [guideKey, loadedDraftKey, packageData, setItinerary]);

  const updatePackage = (updater) => {
    setPackageData((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...next, updated_at: new Date().toISOString() };
    });
    setMessage('');
    setError('');
  };

  const updateConfig = (key, value) => {
    const normalizedValue = key === 'partner_social_handle'
      ? (String(value || '').trim() ? `@${String(value).trim().replace(/^@+/, '').replace(/\s+/g, '')}` : '')
      : value;
    updatePackage((current) => ({
      ...current,
      config: {
        ...current.config,
        [key]: normalizedValue,
        ...(key === 'partner_social_handle'
          ? { social_mentions: ['@guide.sofia', normalizedValue].filter(Boolean).join(' ') }
          : {}),
        ...(key === 'output_language'
          ? { asset_copy: preserveMediaPackageFormatLayouts(current.config.asset_copy) }
          : {}),
      },
      ...(key === 'output_language' ? {
        drafts: MEDIA_CHANNELS.reduce((drafts, channel) => ({ ...drafts, [channel.id]: '' }), {}),
      } : {}),
      generation: {
        ...current.generation,
        ...(key === 'output_language' ? { output_language: '' } : {}),
        stale: true,
      },
    }));
  };

  const updateVisualConfig = (key, value) => {
    updatePackage((current) => {
      const nextValue = typeof value === 'function'
        ? value(current.config[key], current.config)
        : value;
      return {
        ...current,
        config: {
          ...current.config,
          [key]: nextValue,
        },
      };
    });
  };

  const saveCurrentLayoutAsDefault = async () => {
    setLayoutPreferenceStatus('saving');
    try {
      const nextLayout = {
        ...extractMediaPackageLayoutPreset(packageData.config),
        phone: reusablePhoneLayout,
      };
      await saveMediaPackageLayoutPreference(nextLayout);
      setLayoutPreference(nextLayout);
      setLayoutPreferenceStatus('saved');
      window.setTimeout(() => setLayoutPreferenceStatus(''), 2500);
    } catch (layoutError) {
      setLayoutPreferenceStatus('error');
      setError(layoutError.message || 'The default visual layout could not be saved.');
    }
  };

  const applySavedLayoutDefault = () => {
    if (!layoutPreference) return;
    updatePackage((current) => ({
      ...current,
      config: applyMediaPackageLayoutPreset(current.config, layoutPreference),
    }));
    setLayoutPreferenceStatus('applied');
    window.setTimeout(() => setLayoutPreferenceStatus(''), 2500);
  };

  const toggleFeaturedStop = (stopId) => {
    const exists = selectedIds.includes(stopId);
    if (!exists && selectedIds.length >= 3) {
      setError('Select no more than three featured stories.');
      return;
    }

    updateConfig(
      'selected_stop_ids',
      exists ? selectedIds.filter((id) => id !== stopId) : [...selectedIds, stopId]
    );
  };

  const rebuildDrafts = () => {
    if (factsRefreshTimerRef.current) window.clearTimeout(factsRefreshTimerRef.current);
    setFactsRefreshStatus('refreshing');
    updatePackage((current) => refreshMediaPackageFromGuide(itinerary, current));
    window.setTimeout(() => {
      const refreshedAt = new Date();
      setFactsRefreshedAt(refreshedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setFactsRefreshStatus('done');
      setMessage('Guide facts refreshed. Generate each format again to use the latest approved in-depth content.');
      factsRefreshTimerRef.current = window.setTimeout(() => setFactsRefreshStatus('idle'), 3500);
    }, 180);
  };

  const generateChannelWithAi = async (channelId) => {
    const publicCampaignLink = String(packageData.config.short_url || '').trim();
    if (!publicCampaignLink) {
      setChannelErrors((current) => ({
        ...current,
        [channelId]: 'Enter the Public campaign link in Package setup before generating any text.',
      }));
      return;
    }
    if (sourceChanged) {
      setChannelErrors((current) => ({ ...current, [channelId]: 'Refresh the guide facts before generating this format.' }));
      return;
    }
    const blockingErrors = validationErrors.filter((item) => (
      !item.includes('copy is empty')
      && !item.includes('required-field markers')
      && !item.includes('inputs changed')
    ));
    if (blockingErrors.length > 0) {
      setChannelErrors((current) => ({ ...current, [channelId]: `Complete the setup first: ${blockingErrors.join(' ')}` }));
      return;
    }

    setGeneratingChannel(channelId);
    setChannelErrors((current) => ({ ...current, [channelId]: '' }));
    setMessage('');
    setError('');
    try {
      const response = await aiService.getCompletion(
        [
          {
            role: 'system',
            content: 'You are Sofia’s senior cultural editor. Write vivid, intelligent, client-safe public communication grounded only in approved facts. Reject museum voice and generic marketing formulas. Return only valid JSON.',
          },
          {
            role: 'user',
            content: buildAiPrompt(packageData, channelId),
          },
        ],
        AI_MODEL,
        {
          temperature: 0.65,
          maxTokens: 10000,
          reasoningEffort: AI_REASONING_EFFORT,
        }
      );
      const parsed = JSON.parse(cleanJsonResponse(response));
      if (channelId === 'instagram_facebook' && parsed?.drafts?.[channelId]) {
        parsed.drafts[channelId] = finalizeSocialCaption(parsed.drafts[channelId], {
          language: packageData.config.output_language,
          url: publicCampaignLink,
          mentions: packageData.config.social_mentions,
        });
      } else if (parsed?.drafts?.[channelId]) {
        parsed.drafts[channelId] = ensureCaptionLink(
          parsed.drafts[channelId],
          publicCampaignLink,
          packageData.config.output_language
        );
      }
      validatePublicMediaOutput(parsed, channelId, packageData);
      const assetIds = [
        ...MEDIA_ASSET_SPECS.filter((spec) => spec.id !== 'social_link' && spec.channel === channelId).map((spec) => spec.id),
        ...LANDSCAPE_VARIATIONS.filter((variation) => variation.channel === channelId).map((variation) => variation.id),
      ];
      updatePackage((current) => mergeAiMediaChannelDraft(current, parsed, channelId, AI_MODEL, assetIds));
      const generatedLanguage = LANGUAGES.find(({ code }) => code === packageData.config.output_language)?.name || 'the selected language';
      setMessage(`${MEDIA_CHANNELS.find((item) => item.id === channelId)?.label || 'Format'} generated in ${generatedLanguage}.`);
    } catch (generationError) {
      setChannelErrors((current) => ({
        ...current,
        [channelId]: generationError.message || 'This format could not be generated.',
      }));
    } finally {
      setGeneratingChannel('');
    }
  };

  const copyDraft = async (channelId) => {
    try {
      await navigator.clipboard.writeText(packageData.drafts[channelId] || '');
      setCopiedChannel(channelId);
      window.setTimeout(() => setCopiedChannel(''), 1600);
    } catch (copyError) {
      setError(`Copy failed: ${copyError.message}`);
    }
  };

  const downloadJson = () => {
    const configurationFile = createMediaPackageConfigFile({
      guideKey,
      packageData,
      assets: cloudAssetsRef.current,
    });
    const content = `${JSON.stringify(configurationFile, null, 2)}\n`;
    downloadBlob(
      new Blob([content], { type: 'application/json;charset=utf-8' }),
      mediaPackageFilename(packageData).replace(/\.json$/i, '_client_configuration.json')
    );
    setMessage('Versioned client configuration downloaded. Keep it with the client project files.');
  };

  const restoreClientConfiguration = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    try {
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('The configuration file is larger than 10 MB.');
      }
      const restored = parseMediaPackageConfigFile(JSON.parse(await file.text()), guideKey);
      const restoredPackage = createMediaPackage(itinerary, restored.packageData);
      cloudAssetsRef.current = restored.assets;
      setCloudAssets(restored.assets);
      setPackageData({ ...restoredPackage, updated_at: new Date().toISOString() });
      setLocalCampaignImage(restored.assets.campaignImage?.url || restoredPackage.config.campaign_image_url || '');
      setLocalPartnerLogo(restored.assets.partnerLogo?.url || restoredPackage.config.partner_logo_url || '');
      setLocalPhoneFrame(restored.assets.phoneFrame?.url || restoredPackage.config.phone_frame_url || '');
      setLocalPhoneScreen(restored.assets.phoneScreen?.url || restoredPackage.config.phone_screen_url || '');
      setLocalVideoMedia(null);
      setMessage(`Client configuration restored from ${file.name}. Firebase autosave will preserve it for this guide.`);
    } catch (configurationError) {
      setError(configurationError instanceof SyntaxError
        ? 'The selected configuration is not valid JSON.'
        : configurationError.message || 'The client configuration could not be restored.');
    }
  };

  const buildZip = async ({ shouldDownload = true, onProgress, onStage } = {}) => {
    if (sourceChanged) {
      setError('The guide changed after this package was created. Refresh it before export.');
      return;
    }
    setIsExportingZip(true);
    setError('');
    const zip = new JSZip();
    const exportWarnings = validationErrors.map((item) => `Optional or incomplete package item: ${item}`);
    let includedVideoCount = 0;
    const missingRenderedVideos = [];
    const includedVideoSources = new Set();
    onProgress?.(1);
    zip.file('README.txt', buildMediaPackageReadme(packageData));
    zip.file(packageDataFilePath(), `${JSON.stringify(packageData, null, 2)}\n`);
    zip.file(allCopyFilePath(), buildAllCopy(packageData, MEDIA_CHANNELS));
    const addRemoteAsset = async (url, zipPath, { storagePath = '' } = {}) => {
      if (!url && !storagePath) return false;
      let temporaryUrl = '';
      try {
        let downloadedBlob = null;
        if (storagePath) {
          try {
            downloadedBlob = await getMediaPackageStorageBlob(storagePath);
          } catch {
            // Fall back to the tokenized URL for legacy renders or transient SDK errors.
          }
        }
        if (!downloadedBlob) {
          const resolvedUrl = url || await getMediaPackageStorageDownloadUrl(storagePath);
          const accessibleUrl = resolvedUrl;
          temporaryUrl = String(accessibleUrl).startsWith('blob:') ? accessibleUrl : '';
          let response;
          try {
            response = await fetch(accessibleUrl);
          } catch (downloadError) {
            if (!storagePath) throw downloadError;
            response = await fetch(await getMediaPackageStorageDownloadUrl(storagePath));
          }
          if (!response.ok && storagePath) response = await fetch(await getMediaPackageStorageDownloadUrl(storagePath));
          if (!response.ok) {
            const dataUrl = await aiService.getMediaAssetDataUrl(resolvedUrl);
            response = await fetch(dataUrl);
          }
          if (!response.ok) throw new Error(`download returned ${response.status}`);
          downloadedBlob = await response.blob();
        }
        const extensionByType = {
          'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
          'video/mp4': 'mp4', 'audio/mpeg': 'mp3', 'audio/mp3': 'mp3',
        };
        const finalPath = /\.[a-zA-Z0-9]{2,5}$/.test(zipPath)
          ? zipPath
          : `${zipPath}.${extensionByType[downloadedBlob.type] || 'bin'}`;
        zip.file(finalPath, downloadedBlob);
        if (finalPath.toLowerCase().endsWith('.mp4')) includedVideoCount += 1;
        return true;
      } catch (assetError) {
        exportWarnings.push(`${zipPath}: ${assetError.message || 'could not be downloaded'}`);
        return false;
      } finally {
        if (temporaryUrl) URL.revokeObjectURL(temporaryUrl);
      }
    };
    MEDIA_CHANNELS.forEach((channel, index) => {
      zip.file(copyFilePath(channel, index), `${packageData.drafts[channel.id] || ''}\n`);
    });
    const preparedGeneratedAssets = {};
    onStage?.('Rendering fresh image previews for the client package...');
    for (let specIndex = 0; specIndex < MEDIA_ASSET_SPECS.length; specIndex += 1) {
      const spec = MEDIA_ASSET_SPECS[specIndex];
      try {
        const canvas = document.createElement('canvas');
        const formatConfig = mediaAssetConfig(packageData.config, spec.id);
        const blob = await renderAsset(
          canvas,
          spec,
          formatConfig,
          localCampaignImage || packageData.config.campaign_image_url || packageData.guide.thumbnail_url,
          localPartnerLogo || packageData.config.partner_logo_url,
          localPhoneFrame || packageData.config.phone_frame_url,
          localPhoneScreen || packageData.config.phone_screen_url
        );
        preparedGeneratedAssets[spec.id] = blob;
        zip.file(imageFilePath(spec, specIndex), blob);
      } catch (imageError) {
        exportWarnings.push(`${imageFilePath(spec, specIndex)}: ${imageError.message || 'could not be rendered'}`);
      }
      onProgress?.(5 + Math.round(((specIndex + 1) / MEDIA_ASSET_SPECS.length) * 20));
    }
    setGeneratedAssets(preparedGeneratedAssets);

    const storySeries = Array.isArray(packageData.config.instagram_story_series)
      ? packageData.config.instagram_story_series
      : [];
    const storyVideosToCollect = storySeries.filter((story) => (
      story.rendered_video_url || story.rendered_video_storage_path
    ));
    let collectedVideoCount = 0;
    for (let index = 0; index < storySeries.length; index += 1) {
      const story = storySeries[index];
      const storyFolder = storyFolderPath(story, index);
      zip.file(storyCopyFilePath(story, index), [
        `Title: ${story.title || ''}`,
        `Stop: ${story.stop_name || ''}`,
        `Visual headline: ${story.visual_headline || ''}`,
        '',
        'Narration:',
        story.narration || '',
        '',
        'Caption:',
        story.caption || '',
      ].join('\n'));
      const videoSourceKey = story.rendered_video_storage_path || story.rendered_video_url || '';
      if (videoSourceKey) onStage?.(`Adding rendered Story video ${collectedVideoCount + 1} of ${storyVideosToCollect.length} to the ZIP...`);
      const included = videoSourceKey
        ? await addRemoteAsset(story.rendered_video_url, storyVideoFilePath(story, index), {
          storagePath: story.rendered_video_storage_path || '',
        })
        : false;
      if (included && videoSourceKey) includedVideoSources.add(videoSourceKey);
      if (!included && (story.rendered_video_url || story.rendered_video_storage_path)) {
        missingRenderedVideos.push(storyFolder);
      }
      if (!story.rendered_video_url && !story.rendered_video_storage_path) exportWarnings.push(`${storyFolder}: final MP4 has not been rendered yet`);
      if (videoSourceKey) {
        collectedVideoCount += 1;
        onProgress?.(25 + Math.round((collectedVideoCount / Math.max(1, storyVideosToCollect.length)) * 45));
      }
    }

    if (packageData.config.instagram_story_last_render_url || packageData.config.instagram_story_last_render_storage_path) {
      const mainVideoSourceKey = packageData.config.instagram_story_last_render_storage_path
        || packageData.config.instagram_story_last_render_url;
      if (!includedVideoSources.has(mainVideoSourceKey)) {
        onStage?.('Adding the main preview video to the ZIP...');
        await addRemoteAsset(
          packageData.config.instagram_story_last_render_url,
          mainPreviewVideoFilePath(),
          { storagePath: packageData.config.instagram_story_last_render_storage_path || '' }
        );
      }
    }
    try {
      if (exportWarnings.length > 0) {
        zip.file('WARNINGS.txt', `Review the following Media Package export warnings:\n\n${exportWarnings.join('\n')}\n`);
      }
      onStage?.('Assembling the fixed ZIP snapshot...');
      const blob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
        onProgress?.(70 + Math.round((metadata.percent / 100) * 30));
      });
      if (shouldDownload) {
        downloadBlob(blob, mediaPackageFilename(packageData, 'zip'));
        setMessage(`Complete Media Package ZIP created with ${includedVideoCount} rendered MP4${includedVideoCount === 1 ? '' : 's'}. Nothing was published.`);
      }
      return { blob, warnings: exportWarnings, generatedAssets: preparedGeneratedAssets, missingRenderedVideos };
    } finally {
      setIsExportingZip(false);
    }
  };

  const exportBlockingMessage = () => {
    if (sourceChanged) return 'Refresh the Media Package from the guide before exporting or publishing.';
    return '';
  };

  const downloadZip = async () => {
    if (isExportingZip || isPublishing) return;
    const blockingMessage = exportBlockingMessage();
    if (blockingMessage) {
      setError(blockingMessage);
      setPackageActionFeedback({ tone: 'error', text: blockingMessage });
      return;
    }
    setPackageActionFeedback({ tone: 'working', text: 'Collecting final copy, images, and rendered videos...' });
    try {
      const built = await buildZip({ shouldDownload: true });
      if (!built) throw new Error('The ZIP could not be prepared.');
      const warningText = built.warnings.length > 0
        ? ` ${built.warnings.length} optional asset(s) could not be included.`
        : '';
      setPackageActionFeedback({ tone: 'success', text: `ZIP downloaded successfully.${warningText}` });
    } catch (zipError) {
      const feedback = zipError.message || 'The complete ZIP could not be created.';
      setError(feedback);
      setPackageActionFeedback({ tone: 'error', text: feedback });
    } finally {
      setIsExportingZip(false);
    }
  };

  const publishClientPackage = async () => {
    if (isPublishing) return;
    const blockingMessage = exportBlockingMessage();
    if (blockingMessage) {
      setError(blockingMessage);
      setPackageActionFeedback({ tone: 'error', text: blockingMessage });
      return;
    }
    setIsPublishing(true);
    setPublishProgress(0);
    setError('');
    setPackageActionFeedback({ tone: 'working', text: 'Saving the current Media Package draft to Firebase...' });
    try {
      const savedCloud = await saveMediaPackageCloudDraft({
        guideKey,
        packageData,
        localCampaignImage,
        localPartnerLogo,
        localPhoneFrame,
        localPhoneScreen,
        localVideoMedia,
        previousAssets: cloudAssetsRef.current,
      });
      cloudAssetsRef.current = savedCloud.assets;
      setCloudAssets(savedCloud.assets);
      setPackageActionFeedback({ tone: 'working', text: 'Building the fixed client ZIP snapshot...' });
      const built = await buildZip({
        shouldDownload: false,
        onProgress: (progress) => setPublishProgress(5 + Math.round(progress * 0.55)),
        onStage: (text) => setPackageActionFeedback({ tone: 'working', text }),
      });
      if (!built) throw new Error('The client ZIP snapshot could not be prepared.');
      if (built.missingRenderedVideos.length > 0) {
        throw new Error(`Publication stopped because ${built.missingRenderedVideos.length} rendered Story video${built.missingRenderedVideos.length === 1 ? '' : 's'} could not be included in the ZIP. Render the affected Story again and retry.`);
      }
      setPackageActionFeedback({ tone: 'working', text: 'Uploading the client package to Firebase...' });
      const published = await publishMediaPackageCloudSnapshot({
        guideKey,
        packageData,
        assets: savedCloud.assets,
        generatedAssets: built.generatedAssets,
        zipBlob: built.blob,
        warnings: built.warnings,
        onProgress: (progress) => {
          setPublishProgress(60 + Math.round(progress * 0.4));
          setPackageActionFeedback({
            tone: 'working',
            text: progress < 35 ? 'Uploading image previews...' : 'Uploading the fixed ZIP snapshot...',
          });
        },
      });
      setPublishProgress(100);
      setPublishedInfo(published);
      setMessage(`Client Media Package version ${published.version} published. The client now sees this fixed snapshot.`);
      setPackageActionFeedback({ tone: 'success', text: `Published successfully as client version ${published.version}.` });
    } catch (publishError) {
      const feedback = publishError.message || 'The client Media Package could not be published.';
      setError(feedback);
      setPackageActionFeedback({ tone: 'error', text: feedback });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 text-white shadow-sm">
        <div className="grid gap-6 p-6 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div className="flex items-center gap-2 text-sky-300">
              <Megaphone className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em]">Client communications</span>
            </div>
            <h2 className="mt-3 text-2xl font-bold">Media Package</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
              Build editable launch copy from the current guide. This package remains private Builder
              draft data and is stripped from the live guide during publication.
            </p>
          </div>
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
            <div className="font-semibold">Draft only</div>
            <div className="mt-1 text-xs text-amber-200">No automatic client publication</div>
            <div className={`mt-2 text-xs font-semibold ${draftStorageStatus === 'error' ? 'text-rose-300' : 'text-emerald-300'}`}>
              {draftStorageStatus === 'loading' && 'Loading shared Firebase draft...'}
              {draftStorageStatus === 'saving' && 'Saving to Firebase...'}
              {draftStorageStatus === 'saved' && 'Saved to Firebase'}
              {draftStorageStatus === 'ready' && 'Firebase autosave ready'}
              {draftStorageStatus === 'error' && 'Cloud save unavailable · local fallback active'}
            </div>
          </div>
        </div>
      </section>

      {sourceChanged && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span>The guide changed after this media package was created.</span>
          <button
            type="button"
            onClick={rebuildDrafts}
            disabled={factsRefreshStatus === 'refreshing'}
            className="flex items-center gap-2 rounded-lg bg-amber-900 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-800"
          >
            {factsRefreshStatus === 'done' ? <Check className="h-3.5 w-3.5" /> : <RefreshCw className={`h-3.5 w-3.5 ${factsRefreshStatus === 'refreshing' ? 'animate-spin' : ''}`} />}
            {factsRefreshStatus === 'refreshing' ? 'Refreshing…' : factsRefreshStatus === 'done' ? 'Guide facts refreshed' : 'Refresh from guide'}
          </button>
        </div>
      )}

      {message && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {message}
        </div>
      )}
      {error && (
        <div className="flex gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {draftStorageError && (
        <div className="flex gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{draftStorageError}</span>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(20rem,0.8fr)]">
        <CollapsiblePanel
          title="Package setup"
          description="Guide facts are populated automatically. Complete business details that cannot be inferred safely."
        >
          <div className="grid gap-4 md:grid-cols-2">
            {SETUP_FIELDS.map((field) => (
              <Field
                key={field.key}
                field={field}
                value={packageData.config[field.key]}
                onChange={(value) => updateConfig(field.key, value)}
              />
            ))}
          </div>
        </CollapsiblePanel>

        <div className="space-y-6">
          <CollapsiblePanel title="Guide source" description={`${packageData.guide.number_of_stops} approved stops from the current guide.`}>
            <GuideSourceSummary packageData={packageData} />
          </CollapsiblePanel>

          <CollapsiblePanel
            title="Featured stories"
            description={`${selectedIds.length} of 3 selected. Only these approved stories are sent to the media-copy model.`}
          >
            <div className="mt-4 max-h-[28rem] space-y-2 overflow-auto pr-1">
              {packageData.guide.stops.map((stop) => {
                const selected = selectedIds.includes(stop.id);
                return (
                  <button
                    type="button"
                    key={stop.id}
                    onClick={() => toggleFeaturedStop(stop.id)}
                    className={`w-full rounded-xl border p-3 text-left transition ${
                      selected
                        ? 'border-sky-400 bg-sky-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                        selected ? 'border-sky-600 bg-sky-600 text-white' : 'border-slate-300'
                      }`}>
                        {selected && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{stop.name}</span>
                        {stop.description && (
                          <span className="mt-1 line-clamp-3 block text-xs leading-5 text-slate-500">
                            {stop.description}
                          </span>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 text-xs font-medium text-slate-500">
              {selectedIds.length} of 3 selected
            </div>
          </CollapsiblePanel>

        </div>
      </div>

      <div className="order-[100]">
          <CollapsiblePanel title="Package actions" description="Refresh, export, or publish the current Media Package.">
            <div className="space-y-2">
              <button
                type="button"
                onClick={rebuildDrafts}
                disabled={factsRefreshStatus === 'refreshing'}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {factsRefreshStatus === 'done' ? <Check className="h-4 w-4 text-emerald-600" /> : <RefreshCw className={`h-4 w-4 ${factsRefreshStatus === 'refreshing' ? 'animate-spin' : ''}`} />}
                {factsRefreshStatus === 'refreshing' ? 'Refreshing guide facts…' : factsRefreshStatus === 'done' ? 'Guide facts refreshed' : 'Refresh guide facts'}
              </button>
              {factsRefreshedAt && <div className="text-center text-xs font-medium text-emerald-700">Last refreshed at {factsRefreshedAt}</div>}
              <button
                type="button"
                onClick={downloadJson}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <FileJson className="h-4 w-4" />
                Download client configuration
              </button>
              <input
                ref={configurationFileInputRef}
                className="hidden"
                type="file"
                accept="application/json,.json"
                onChange={restoreClientConfiguration}
              />
              <button
                type="button"
                onClick={() => configurationFileInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-sky-300 bg-sky-50 px-4 py-2.5 text-sm font-semibold text-sky-800 hover:bg-sky-100"
              >
                <Upload className="h-4 w-4" />
                Restore client configuration
              </button>
              <button
                type="button"
                onClick={downloadZip}
                disabled={isExportingZip || isPublishing}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:bg-slate-400"
              >
                {isExportingZip ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                {isExportingZip ? 'Building complete ZIP...' : 'Download everything as ZIP'}
              </button>
              <button
                type="button"
                onClick={publishClientPackage}
                disabled={isPublishing || isExportingZip}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:bg-slate-400"
              >
                {isPublishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {isPublishing ? `Publishing ${publishProgress}%...` : publishedInfo ? 'Publish a new client version' : 'Publish for client'}
              </button>
              {packageActionFeedback && (
                <div className={`rounded-lg border px-3 py-2.5 text-xs font-medium leading-5 ${packageActionFeedback.tone === 'error'
                  ? 'border-rose-200 bg-rose-50 text-rose-800'
                  : packageActionFeedback.tone === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-sky-200 bg-sky-50 text-sky-800'}`}
                >
                  {packageActionFeedback.tone === 'working' && <Loader2 className="mr-2 inline h-3.5 w-3.5 animate-spin" />}
                  {packageActionFeedback.text}
                </div>
              )}
            </div>
            <p className="mt-3 text-xs leading-5 text-slate-500">
              Download a client configuration after visual approval. It can restore that guide's exact layout and asset references later. Publishing creates a fixed Firebase snapshot; later draft edits remain private until you publish again.
            </p>
            {publishedInfo && <p className="mt-2 text-xs font-semibold text-emerald-700">Published version {publishedInfo.version} · {new Date(publishedInfo.publishedAt).toLocaleString()}</p>}
          </CollapsiblePanel>

      </div>

      <div className="order-[90]">
          <CollapsiblePanel
            title={validationErrors.length === 0 ? 'Ready for review export' : `${validationErrors.length} optional item(s) empty or incomplete`}
            description={validationErrors.length === 0 ? 'No package issues were found.' : 'These items will be listed as warnings, but they do not block download or publication.'}
            tone={validationErrors.length === 0 ? 'emerald' : 'amber'}
          >
            {validationErrors.length > 0 && (
              <ul className="space-y-1.5 text-xs leading-5 text-amber-900">
                {validationErrors.slice(0, 12).map((item) => <li key={item}>- {item}</li>)}
                {validationErrors.length > 12 && <li>- And {validationErrors.length - 12} more.</li>}
              </ul>
            )}
          </CollapsiblePanel>
      </div>

      <div className="order-[60]">
      <CollapsiblePanel
        title="Launch and partner calendar"
        description="Automatic timeline around the launch date, with recommended actions before and after publication."
        tone="indigo"
      >
        <MediaLaunchCalendar
          compact
          launchDate={packageData.config.launch_date}
          partnerHandle={packageData.config.partner_social_handle}
          items={packageData.config.promotion_calendar}
          onChange={(items) => updateVisualConfig('promotion_calendar', items)}
        />
      </CollapsiblePanel>
      </div>

      <div className="order-[40]">
      <MediaAssetStudio
        storyEditor={(
        <InstagramStorySeriesStudio
          compact
          itinerary={itinerary}
          packageData={packageData}
          onConfigChange={updateVisualConfig}
          onError={setError}
          workspaceId={workspaceId}
          campaignImageSource={localCampaignImage || packageData.config.campaign_image_url || packageData.guide.thumbnail_url}
          partnerLogoSource={localPartnerLogo || packageData.config.partner_logo_url}
        />
        )}
        packageData={packageData}
        onConfigChange={updateVisualConfig}
        onAssetsChange={setGeneratedAssets}
        onError={setError}
        localCampaignImage={localCampaignImage}
        onLocalCampaignImageChange={setLocalCampaignImage}
        localPartnerLogo={localPartnerLogo}
        onLocalPartnerLogoChange={setLocalPartnerLogo}
        localPhoneFrame={localPhoneFrame}
        onLocalPhoneFrameChange={setLocalPhoneFrame}
        localPhoneScreen={localPhoneScreen}
        onLocalPhoneScreenChange={setLocalPhoneScreen}
        workspaceId={workspaceId}
        storyVideoUrl={storyMediaType.startsWith('video/') ? storyMediaUrl : ''}
        storyVideoFile={localVideoMedia?.type?.startsWith('video/') ? localVideoMedia : null}
        storyVideoStoragePath={storyMediaType.startsWith('video/') ? cloudAssets.storyMedia?.storagePath || '' : ''}
        storyVideoFingerprint={storyMediaType.startsWith('video/') ? cloudAssets.storyMedia?.fingerprint || '' : ''}
        storyPhotoUrl={storyMediaType.startsWith('image/') ? storyMediaUrl : ''}
        storyPhotoFile={localVideoMedia?.type?.startsWith('image/') ? localVideoMedia : null}
        storyAudioUrl={activeInstagramStory?.audio_url || ''}
        storyAudioLabel={activeInstagramStory?.title || ''}
        onSaveLayoutDefault={saveCurrentLayoutAsDefault}
        onApplyLayoutDefault={applySavedLayoutDefault}
        hasLayoutDefault={Boolean(layoutPreference)}
        layoutPreferenceStatus={layoutPreferenceStatus}
      />
      </div>

      <div className="order-[20]">
      <CollapsiblePanel
        title="Editable channel drafts"
        description={`Generation mode: ${packageData.generation.mode}${packageData.generation.model ? ` | ${packageData.generation.model}` : ''}`}
      >
        <div className="grid gap-5 xl:grid-cols-2">
          {MEDIA_CHANNELS.map((channel) => (
            <DraftCard
              key={channel.id}
              channel={channel}
              value={packageData.drafts[channel.id]}
              instructions={packageData.config.channel_instructions?.[channel.id] || ''}
              onInstructionsChange={(value) => updateVisualConfig('channel_instructions', (current = {}) => ({
                ...(current || {}),
                [channel.id]: value,
              }))}
              copied={copiedChannel === channel.id}
              onCopy={() => copyDraft(channel.id)}
              onGenerate={() => generateChannelWithAi(channel.id)}
              isGenerating={generatingChannel === channel.id}
              generationError={channelErrors[channel.id]}
              onChange={(value) => updatePackage((current) => ({
                ...current,
                drafts: {
                  ...current.drafts,
                  [channel.id]: value,
                },
              }))}
            />
          ))}
        </div>
      </CollapsiblePanel>
      </div>
    </div>
  );
};

export default MediaPackageTab;
